/**
 * Cross-feature dashboard query.
 *
 * The `DashboardSnapshot` is the canonical resource catalog the hub uses
 * everywhere — the dashboard cards on `/`, the master list on `/resources`,
 * and (eventually) the elevation request form. It lives under `lib/`
 * instead of `features/dashboard/` so other features can consume it without
 * an `@/features/<other>` cross-feature import (which the repo guidelines
 * forbid).
 */

import { useQuery } from '@tanstack/react-query';

import { hubFetch } from '@/api/client';
import type { DashboardSnapshot } from '@/api/types';
import { useEnvStore } from '@/env/envStore';

const DEFAULT_WINDOW_DAYS = 30;
const REFETCH_INTERVAL_MS = 60_000;
const STALE_MS = 30_000;

export function useDashboardHealth(windowDays = DEFAULT_WINDOW_DAYS, enabled = true) {
  const env = useEnvStore((s) => s.env);
  return useQuery({
    queryKey: ['dashboard', 'health', env, windowDays],
    queryFn: () =>
      hubFetch<DashboardSnapshot>('/api/dashboard/health', {
        searchParams: { windowDays },
      }),
    enabled,
    refetchInterval: enabled ? REFETCH_INTERVAL_MS : false,
    staleTime: STALE_MS,
  });
}
